import React, { useRef, useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    setSelectedRegion,
    setSelectedDistrict,
    setSelectedPoint,
    setFilter,
} from "../../../store/pointsSlice";
import { formatCo2, getCo2Totals } from "../../../utils/co2";
import "./RegionsList.css";

const RegionsList = ({ districtId }) => {
    const dispatch = useDispatch();
    const points = useSelector((state) => state.points.points);
    const selectedRegion = useSelector((state) => state.points.selectedRegion);
    const scrollContainerRef = useRef(null);
    const containerRef = useRef(null);
    const selectedYear = useSelector(
        (state) => state.points.selectedYear || "2030"
    );
    const co2Totals = useMemo(
        () => getCo2Totals(points, selectedYear),
        [points, selectedYear]
    );
    const [regionExtensions, setRegionExtensions] = useState({});

    const [currentPage, setCurrentPage] = useState(0);
    const [regionsPerPage, setRegionsPerPage] = useState(2);

    useEffect(() => {
        const calculateVisibleRegions = () => {
            if (!containerRef.current) return;

            const containerWidth = containerRef.current.offsetWidth;
            const regionCardWidth = 300;
            const maxRegionsInRow = Math.floor(
                containerWidth / regionCardWidth
            );

            setRegionsPerPage(maxRegionsInRow);
        };

        calculateVisibleRegions();
        window.addEventListener("resize", calculateVisibleRegions);

        return () =>
            window.removeEventListener("resize", calculateVisibleRegions);
    }, []);

    const getCO2Value = (regionName) => {
        const value = co2Totals.regions[regionName];
        if (!value) return "по запросу";
        return formatCo2(value);
    };

    const getObjectWord = (count) => {
        const lastDigit = count % 10;
        const lastTwoDigits = count % 100;

        if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return "объектов";
        if (lastDigit === 1) return "объект";
        if (lastDigit >= 2 && lastDigit <= 4) return "объекта";
        return "объектов";
    };

    // console.log('RegionsList - render:', {
    //     districtId,
    //     selectedRegion,
    //     pointsCount: points.length
    // });

    const regions = Array.from(
        new Set(
            points
                .filter((point) => {
                    const district = point.federalDistrict?.replace(
                        " Федеральный Округ",
                        ""
                    );
                    return district === districtId;
                })
                .map((point) => point.region)
        )
    ).filter(Boolean);

    const paginatedRegions = regions.reduce((result, region, index) => {
        const pageIndex = Math.floor(index / regionsPerPage);
        if (!result[pageIndex]) {
            result[pageIndex] = [];
        }
        result[pageIndex].push(region);
        return result;
    }, []);

    useEffect(() => {
        setCurrentPage(0);
    }, [regionsPerPage]);

    const handleRegionClick = (region) => {
        dispatch(setSelectedRegion(region));
        dispatch(setSelectedPoint(null));
        dispatch(
            setFilter({
                region: region,
                fromBottomPanel: true,
            })
        );
    };

    const scrollLeft = () => {
        scrollContainerRef.current.scrollBy({ left: -300, behavior: "smooth" });
    };

    const scrollRight = () => {
        scrollContainerRef.current.scrollBy({ left: 300, behavior: "smooth" });
    };

    const getRegionObjectsCount = (regionId) => {
        const regionPoints = points.filter(
            (point) => point.region === regionId
        );
        return regionPoints.length;
    };

    const handleBackClick = () => {
        dispatch(setSelectedRegion(null));
        dispatch(setSelectedDistrict(null));
        dispatch(setFilter({ region: "all" }));

        const mapElement = document.querySelector(".map-container svg");
        if (mapElement) {
            const paths = mapElement.getElementsByTagName("path");
            Array.from(paths).forEach((path) => {
                path.style.fill = "#D3D3D3";
            });
        }
    };

    const nextPage = () => {
        if (currentPage < paginatedRegions.length - 1) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 0) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    return (
        <div className="regions-list-container">
            <button
                className="scroll-button left"
                onClick={prevPage}
                disabled={currentPage === 0}
            >
                ←
            </button>
            <div className="regions-list" ref={containerRef}>
                {paginatedRegions[currentPage]?.map((region) => (
                    <div
                        key={region}
                        className={`region-item ${
                            selectedRegion === region ? "active" : ""
                        }`}
                        onClick={() => handleRegionClick(region)}
                    >
                        <div className="region-card">
                            <div className="region-info">
                                <h4>{region}</h4>
                                <p>
                                    {getRegionObjectsCount(region)}{" "}
                                    {getObjectWord(
                                        getRegionObjectsCount(region)
                                    )}
                                </p>
                                <p className="co2-text">
                                    Потенциал поглощения CO₂:{" "}
                                    {getCO2Value(region)}
                                </p>
                            </div>
                            <div className="region-icon-placeholder">
                                <img
                                    src={`/arms/${region}.${
                                        regionExtensions[region] || "svg"
                                    }`}
                                    alt={`Герб ${region}`}
                                    className="region-coat-of-arms"
                                    onError={(e) => {
                                        const currentExt =
                                            regionExtensions[region] || "svg";
                                        switch (currentExt) {
                                            case "svg":
                                                setRegionExtensions((prev) => ({
                                                    ...prev,
                                                    [region]: "png",
                                                }));
                                                break;
                                            case "png":
                                                setRegionExtensions((prev) => ({
                                                    ...prev,
                                                    [region]: "webp",
                                                }));
                                                break;
                                            case "webp":
                                                setRegionExtensions((prev) => ({
                                                    ...prev,
                                                    [region]: "jpg",
                                                }));
                                                break;
                                            case "jpg":
                                                // console.log(`Failed to load coat of arms for ${region}`);
                                                e.target.style.display = "none";
                                                break;
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <button
                className="scroll-button right"
                onClick={nextPage}
                disabled={currentPage === paginatedRegions.length - 1}
            >
                →
            </button>
            <button className="back-button" onClick={handleBackClick}>
                Назад к округам
            </button>
        </div>
    );
};

export default RegionsList;
